import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import setTitle from "../../../components/common/SetTitle";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import { OutletIProps } from "../../../components/sitebase/Base.types";
import { INLINE_MENU_LINKS } from "./InlineMenu";

type InlineRuleListItem = {
    id: number;
    name: string;
    layer_name: string;
    active: boolean;
    latest_forecast_date: string;
    notification_grouping: string | null;
};

export function InlineRulesList() {
    const context = useOutletContext<OutletIProps>();
    const { setExtraMenuLinks } = context;
    const [rules, setRules] = useState<InlineRuleListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        setTitle("INLINE Rules");
        setExtraMenuLinks(INLINE_MENU_LINKS, "Rules");
    }, [setExtraMenuLinks]);

    useEffect(() => {
        setLoading(true);

        const headers: Record<string, string> = {};
        if (context.user.token) {
            headers["Authorization"] = `Token ${context.user.token}`;
        }

        fetch("/api/inline/rules/", { method: "GET", headers, credentials: "include" })
            .then(async (response) => {
                const data = await response.json().catch(() => null);
                if (!response.ok) {
                    const message = data?.message || data?.detail || JSON.stringify(data) || "Unknown error";
                    throw new Error(`HTTP ${response.status}: ${message}`);
                }
                const list = Array.isArray(data) ? data : (data?.results || []);
                return list as InlineRuleListItem[];
            })
            .then((data) => setRules(data))
            .catch((e) => {
                console.error("Failed to load INLINE rules:", e);
                toast.error(`Failed to load rules: ${e instanceof Error ? e.message : String(e)}`);
            })
            .finally(() => setLoading(false));
    }, [context.user.token]);

    return (
        <div className="container py-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="m-0">Rules</h3>
                <Link className="btn btn-outline-success" to="./new">Create rule</Link>
            </div>

            {loading ? (
                <LoadingSpinner />
            ) : rules.length === 0 ? (
                <div className="alert alert-info">No rules found.</div>
            ) : (
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Layer</th>
                            <th>Grouping</th>
                            <th>Active</th>
                            <th>Latest forecast</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map((r) => (
                            <tr key={r.id}>
                                <td><Link className="text-warning fw-bold" to={`./${r.id}`}>{r.name}</Link></td>
                                <td>{r.layer_name}</td>
                                <td>{r.notification_grouping ?? "—"}</td>
                                <td>{String(r.active)}</td>
                                <td>{r.latest_forecast_date}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

